import { ShareLandingLayout, type CarouselMode } from "./ShareLandingLayout";
import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { userErrorMessage } from "@/lib/user-error";
import { MyApplicationsShareRow } from "@/pages/share/applications/MyApplicationsShareRow";
import { OpenListingsShareRow } from "@/pages/share/applications/OpenListingsShareRow";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { NoBackend } from "@/components/common/NoBackend";
import { buttonClasses } from "@/components/design/button-classes";
import {
  ShareItemCardCover,
  ShareItemCardSkeleton,
} from "@/components/share/ShareItemCard";
import { SharedItemsDocument } from "@/graphql/generated/operations";
import { sortOrderFor } from "@/lib/share-sort";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useMySharedItems } from "@/lib/use-my-shared-items";
import { useAssistantTour } from "@/lib/use-assistant-tour";
import { MyProjectsDocument } from "@/graphql/generated/operations";
import { HorizontalCardCarousel } from "@/components/design/HorizontalCardCarousel";
import { ShareCreateEntryChoice } from "@/components/share/ShareCreateEntryChoice";

// SHARE landing - node-by-node port of workshop's screens/Share.jsx.
// Hero + Entdecken carousel (4 curated top-5 lists) + 'Beobachtet &
// Beteiligt' (items the user pinned or signed up to) + 'Meine Projekte
// & Posts' (own items). The last two are horizontally-scrolling rows.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Item = any;

export function SharePage() {
  const navigate = useNavigate();
  const intl = useIntl();
  // Eigene Projekte. Sichtbarkeit steckt in genesis (ProjectVisibilityScope), die Abfrage muss
  // also nicht selbst filtern - sie bekommt nur, was der Principal sehen darf.
  const projectsQ = useQuery(MyProjectsDocument, { variables: { first: 8 } });
  const myProjects = projectsQ.data?.projects.nodes ?? [];
  const canManageListings = useHasFeature(FEATURE_CATALOG.shareListings.key);
  const canCreateProject = useHasFeature(FEATURE_CATALOG.shareProjects.key);
  // grill-me session (2026-09): the "what do you want to make" choice now
  // gates BEFORE navigating away from SHARE, not after landing on
  // /share/create - clicking Create shows the popup right here, over this
  // page, then the choice travels along as a `?type=` param.
  const [entryChoiceOpen, setEntryChoiceOpen] = useState(false);

  // take must cover the whole table: the 4 curated lists (new/top/interest/
  // discussed) sort this same batch client-side, so a low take silently
  // drops recent items once the table grows past it (they never make it
  // into the "new" list even though they're the actual newest).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const allInput: any = { take: 500 };
  const { data, loading, error } = useQuery(SharedItemsDocument, { variables: { input: allInput } });

  const mine = useMySharedItems();
  const { total: myTotal, loading: myLoading, error: myError } = mine;
  const myItems = mine.items as Item[];

  // Like the own-items row (see useMySharedItems), this is one page of a longer list: without
  // `order` genesis pages oldest first and `take` cuts off the newest (spark#945).
  // 'Beobachtet & Beteiligt' = pinned (Beobachtet) oder signedUp (Interessent) -
  // beide sind die einzigen aktivitaetsbezogenen Per-User-Flags, die das Schema
  // filterbar macht (kein dediziertes watched-Flag, vgl. ShareItemCard chips).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activityInput: any = {
    take: 8,
    where: { or: [{ pinned: { eq: true } }, { signedUp: { eq: true } }] },
    order: sortOrderFor("new"),
  };
  const {
    data: activityData,
    loading: activityLoading,
    error: activityError,
  } = useQuery(SharedItemsDocument, { variables: { input: activityInput } });
  const activityItems = (activityData?.sharedItems?.nodes ?? []) as Item[];
  const activityTotal = activityData?.sharedItems?.totalCount ?? null;
  // Bewerben hat keinen Menüpunkt; diese beiden Zeilen sind der Einstieg, nur mit share.applications.
  const canApply = useHasFeature(FEATURE_CATALOG.shareApplications.key);

  const [mode, setMode] = useState<CarouselMode>("new");
  const [idx, setIdx] = useState(0);

  const lists: Record<CarouselMode, Item[]> = useMemo(() => {
    const items = (data?.sharedItems?.nodes ?? []) as Item[];
    const ts = (d: string | null | undefined) => (d ? Date.parse(d) || 0 : 0);
    const by = (fn: (a: Item, b: Item) => number) => [...items].sort(fn).slice(0, 5);
    return {
      top: by((a, b) => (b.rating ?? 0) - (a.rating ?? 0)),
      new: by((a, b) => ts(b.publishedOn) - ts(a.publishedOn)),
      interest: by((a, b) => (b.signUps ?? 0) - (a.signUps ?? 0)),
      discussed: by((a, b) => (b.comments ?? 0) - (a.comments ?? 0)),
    };
  }, [data?.sharedItems?.nodes]);

  const list = lists[mode] ?? [];
  const featured = list[idx] ?? list[0];
  // Only while there is genuinely nothing to show: once an item is featured, a background refetch
  // must not replace a populated hero with placeholders.

  const selectMode = (m: CarouselMode) => {
    setMode(m);
    setIdx(0);
  };
  const go = (delta: number) =>
    setIdx((i) => (list.length ? (i + delta + list.length) % list.length : 0));

  // idx is in the deps so the countdown restarts on every slide change -
  // including manual ones (arrows, dots, tab switch). Without it a manual jump
  // could be cut short by an about-to-fire tick. Effectively a 7s timeout that
  // re-arms after each change.
  useEffect(() => {
    if (list.length < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % list.length), 7000);
    return () => clearInterval(t);
  }, [mode, list.length, idx]);

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the Share feed the user is browsing.
  useAssistantContext(
    data
      ? [
          {
            description: "the Share community feed",
            value:
              `browsing the "${mode}" list` +
              (featured ? `; featured item: "${featured.name}"` : "") +
              `; ${myItems.length} of the shown items are the user's own`,
          },
        ]
      : null,
  );

  // Guided tour (/tour): the Share landing — discover carousel, create CTA, activity + own items.
  useAssistantTour(
    data
      ? {
          label: intl.formatMessage({
            id: "share.landing.tour.label",
            defaultMessage: "Share",
          }),
          summary: featured
            ? intl.formatMessage(
                {
                  id: "share.landing.tour.summary",
                  defaultMessage:
                    "This is Share, the community feed where people publish projects and posts, gather interested people and feedback. Right now \"{name}\" is in the spotlight.",
                },
                { name: featured.name },
              )
            : intl.formatMessage({
                id: "share.landing.tour.emptySummary",
                defaultMessage:
                  "This is Share, the community feed where people publish projects and posts, gather interested people and feedback.",
              }),
          stops: [
            {
              anchor: "share.carousel",
              title: intl.formatMessage({
                id: "share.landing.tour.carousel.title",
                defaultMessage: "Discover",
              }),
              description: intl.formatMessage(
                {
                  id: "share.landing.tour.carousel.desc",
                  defaultMessage:
                    "Four curated top-5 lists: top rated, newest, most interested, most comments. Switch lists with the tabs; currently showing \"{featured}\". Click Open to jump into an item.",
                },
                { featured: featured?.name ?? "–" },
              ),
            },
            {
              anchor: "share.create",
              title: intl.formatMessage({
                id: "share.landing.tour.create.title",
                defaultMessage: "Create something",
              }),
              description: intl.formatMessage({
                id: "share.landing.tour.create.desc",
                defaultMessage:
                  "Publish your own project or post here. Describe it, add images, and collect interested people.",
              }),
            },
            {
              anchor: "share.activity",
              title: intl.formatMessage({
                id: "share.landing.tour.activity.title",
                defaultMessage: "Watched & involved",
              }),
              description: countDescription(activityTotal, {
                unknown: intl.formatMessage({
                  id: "share.landing.tour.activity.unknownDesc",
                  defaultMessage: "Items you watch or sign up for collect here, so you can get back to them quickly.",
                }),
                empty: intl.formatMessage({
                  id: "share.landing.tour.activity.emptyDesc",
                  defaultMessage:
                    "Items you watch or sign up for will collect here; nothing yet. Open an item and hit Watch or Express interest.",
                }),
                some: (count) =>
                  intl.formatMessage(
                    {
                      id: "share.landing.tour.activity.desc",
                      defaultMessage:
                        "The {count} items you are watching or signed up for, so you can get back to them quickly.",
                    },
                    { count },
                  ),
              }),
            },
            {
              anchor: "share.mine",
              title: intl.formatMessage({
                id: "share.landing.tour.mine.title",
                defaultMessage: "My projects & posts",
              }),
              description: countDescription(myTotal, {
                unknown: intl.formatMessage({
                  id: "share.landing.tour.mine.unknownDesc",
                  defaultMessage:
                    "Your own published items appear here. 'View all' opens the full list where you can manage them.",
                }),
                empty: intl.formatMessage({
                  id: "share.landing.tour.mine.emptyDesc",
                  defaultMessage: "Your own published items will appear here. You haven't created any yet.",
                }),
                some: (count) =>
                  intl.formatMessage(
                    {
                      id: "share.landing.tour.mine.desc",
                      defaultMessage:
                        "Your own {count} published items. 'View all' opens the full list where you can manage them.",
                    },
                    { count },
                  ),
              }),
            },
          ],
        }
      : null,
  );

  // Reserve room for the hover reveal of the last horizontal card row, so its details can be
  // scrolled fully into view instead of ending flush with the app's scroll viewport.
  return (
    <ShareLandingLayout mode={mode} selectMode={selectMode} list={list} idx={idx} setIdx={setIdx} go={go}
      loading={loading} error={error} onCreate={() => setEntryChoiceOpen(true)}
      onDiscoverAll={() => navigate("/extended-search?type=SHARE")}>
      {entryChoiceOpen ? (
        <ShareCreateEntryChoice
          canCreateListing={canManageListings}
          onChoose={(choice) =>
            navigate(choice === "listing" ? "/share/listings/new" : `/share/create?type=${choice}`)
          }
          onCancel={() => setEntryChoiceOpen(false)}
        />
      ) : null}

      <Section
        label={intl.formatMessage({
          id: "share.landing.watchedSection",
          defaultMessage: "Watched & involved",
        })}
        right={intl.formatMessage({
          id: "share.landing.myActivity",
          defaultMessage: "My activity",
        })}
        anchor="share.activity"
      >
        <ItemRow
          idPrefix="share-activityitem"
          items={activityItems}
          loading={activityLoading}
          error={activityError}
          emptyLabel={intl.formatMessage({
            id: "share.landing.nothingWatched",
            defaultMessage: "Nothing watched or involved yet",
          })}
          emptyHint={intl.formatMessage({
            id: "share.landing.nothingWatchedHint",
            defaultMessage: "save a project or apply",
          })}
        />
      </Section>

      {canApply || canManageListings ? (
        <Section
          label={intl.formatMessage({
            id: "share.landing.openListingsSection",
            defaultMessage: "Offene Ausschreibungen",
          })}
          right={canApply ? intl.formatMessage({ id: "share.landing.viewAll", defaultMessage: "View all ↗" }) : undefined}
          to={canApply ? "/share/open-listings" : undefined}
          anchor="share.openListings"
          actions={canManageListings ? (
            <Link
              id="share-listings-manage"
              to="/share/listings"
              className={buttonClasses({ variant: "secondary", size: "compact" })}
            >
              <FormattedMessage id="share.listings.post.manage" defaultMessage="Verwalten" />
            </Link>
          ) : undefined}
        >
          {canApply ? <OpenListingsShareRow /> : null}
        </Section>
      ) : null}
      {canApply ? (
        <Section
          label={intl.formatMessage({
            id: "share.landing.applicationsSection",
            defaultMessage: "Meine Bewerbungen",
          })}
          right={intl.formatMessage({ id: "share.landing.viewAll", defaultMessage: "View all ↗" })}
          to="/share/applications"
          anchor="share.applications"
        >
          <MyApplicationsShareRow />
        </Section>
      ) : null}

      <Section
        label={intl.formatMessage({
          id: "share.landing.myProjectsSection",
          defaultMessage: "My projects & posts",
        })}
        right={intl.formatMessage({
          id: "share.landing.viewAll",
          defaultMessage: "View all ↗",
        })}
        to="/share/mine"
        anchor="share.mine"
      >
        <ItemRow
          idPrefix="share-mineitem"
          items={myItems}
          loading={myLoading}
          error={myError}
          emptyLabel={intl.formatMessage({
            id: "share.landing.noOwnPosts",
            defaultMessage: "No own posts yet",
          })}
          emptyHint={intl.formatMessage({
            id: "share.landing.noOwnPostsHint",
            defaultMessage: "create a project or a post",
          })}
        />
      </Section>

      <Section
        label={intl.formatMessage({
          id: "share.landing.projectsSection",
          defaultMessage: "Meine Projekte",
        })}
        right={intl.formatMessage(
          { id: "share.landing.projectsCount", defaultMessage: "{count, plural, one {# Projekt} other {# Projekte}}" },
          { count: projectsQ.data?.projects.totalCount ?? myProjects.length },
        )}
        anchor="share.projects"
      >
        {projectsQ.loading && myProjects.length === 0 ? (
          <div className="box bg-surface p-5 field-meta text-ink-muted" aria-busy="true">
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          </div>
        ) : myProjects.length === 0 ? (
          <NoBackend
            id="share-projects-empty"
            label={intl.formatMessage({ id: "share.landing.noProjects", defaultMessage: "Noch kein Projekt" })}
            hint={
              canCreateProject
                ? intl.formatMessage({
                    id: "share.landing.noProjectsHint",
                    defaultMessage: "lege eines über den Erstellen-Knopf an",
                  })
                : undefined
            }
          />
        ) : (
          /* Gleiche Reihe wie "Meine Projekte & Posts" darüber: derselbe
             HorizontalCardCarousel, dieselbe Kartenbreite, derselbe Hover.
             Nur ohne Bild - ein Projekt hat keins, und ein erfundenes wäre
             eine Behauptung über Inhalte, die es nicht gibt. */
          <HorizontalCardCarousel>
            {myProjects.map((project) => (
              <Link
                key={project.id}
                id={`share-project-${project.id}`}
                to={`/project/${project.id}`}
                className="box block w-64 shrink-0 bg-surface p-4 transition hover:-translate-y-0.5"
              >
                <span className="field-label block text-ink-muted">
                  <FormattedMessage id="share.landing.projectBadge" defaultMessage="Projekt" />
                </span>
                <span className="mt-2 block field-value font-medium">{project.name}</span>
                {/* Wie die Karte eines Unternehmensbeitrags nennt die Karte nur das Unternehmen
                    (@adr SPARK-0042-company-post-detail-shows-company-and-operator@2). */}
                {project.company ? (
                  <span id={`share-project-${project.id}-company`} className="mt-1 block field-meta text-ink-muted">
                    {project.company.name}
                  </span>
                ) : null}
                {project.description ? (
                  <span className="mt-1 block field-meta line-clamp-3 text-ink">{project.description}</span>
                ) : null}
                {project.createdAt ? (
                  <span className="mt-3 block field-meta text-ink-muted">
                    <FormattedDate value={project.createdAt} day="2-digit" month="2-digit" year="numeric" />
                  </span>
                ) : null}
              </Link>
            ))}
          </HorizontalCardCarousel>
        )}
      </Section>
    </ShareLandingLayout>
  );
}

// Loading state of the hero's text column. Mirrors the real blocks (label, two-line title,
// two-line copy, four stats, two actions) so the box keeps its height and nothing shifts when the
// data lands. The tabs above stay live, they never depended on the query.
function countDescription(
  total: number | null,
  texts: { unknown: string; empty: string; some: (count: number) => string },
): string {
  if (total === null) return texts.unknown;
  return total === 0 ? texts.empty : texts.some(total);
}

function ItemRow({
  idPrefix,
  items,
  loading,
  error,
  emptyLabel,
  emptyHint,
}: {
  idPrefix: string;
  items: Item[];
  loading: boolean;
  error: unknown;
  emptyLabel: string;
  emptyHint: string;
}) {
  const intl = useIntl();
  // Placeholder count is the widest row a desktop viewport shows, so the row fills its width
  // while loading instead of growing into it.
  if (loading && items.length === 0) {
    return (
      <div aria-busy="true">
        <HorizontalCardCarousel>
          {Array.from({ length: 6 }, (_, i) => (
            <ShareItemCardSkeleton key={i} className="w-64 shrink-0" />
          ))}
        </HorizontalCardCarousel>
      </div>
    );
  }
  if (error && items.length === 0) {
    return (
      <p role="alert" className="text-smycles-label text-ink-muted">
        <FormattedMessage
          id="share.landing.error"
          defaultMessage="Error: {message}"
          values={{ message: userErrorMessage(intl, error) }}
        />
      </p>
    );
  }
  if (!loading && items.length === 0) {
    return <NoBackend label={emptyLabel} hint={emptyHint} />;
  }
  return (
    <HorizontalCardCarousel>
      {items.map((p, i) => (
        <Link
          key={p.id}
          id={`${idPrefix}-${p.id}`}
          to={`/share/discover/${p.id}`}
          className="block w-64 shrink-0 transition hover:-translate-y-0.5"
        >
          <ShareItemCardCover item={p} index={i} heroImageSrc="/heroes/share.png" />
        </Link>
      ))}
    </HorizontalCardCarousel>
  );
}

function Section({
  label,
  right,
  to,
  anchor,
  actions,
  children,
}: {
  label: string;
  right?: string;
  actions?: ReactNode;
  to?: string;
  /** Assistant anchor (HighlightElement tool + /tour) — on the section root, so the whole
   *  region gets the highlight outline. */
  anchor?: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-10" data-assistant-anchor={anchor}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-smycles-micro tracking-[0.25em] text-ink-muted">{label}</div>
        <div className="flex flex-wrap items-center gap-4">
          {actions}
          {to ? (
            <Link
              to={to}
              className="text-smycles-micro uppercase tracking-[0.2em] text-ink-muted transition hover:text-ink"
            >
              {right}
            </Link>
          ) : right ? (
            <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">{right}</div>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
}
